import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Pencil, Trash2, Palette } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, toast, ConfirmationModal } from '../../components/v2/V2Components';
import PaintService from '../../services/PaintService';

const PAGE_SIZE = 50;

/**
 * Paint Library page — lists every paint the user has created, regardless of
 * which rooms reference them. Provides the canonical place to add a paint
 * without attaching it to a specific room, and to delete paints entirely
 * (which cascades all RoomPaint references AND the gallery's PaintImage rows
 * + their R2 objects).
 *
 * This is also the "rescue page" for paints that aren't attached to any room
 * anymore. RoomDetails only shows paints currently applied somewhere; if a
 * user detaches the last reference, the paint becomes invisible from room
 * pages but persists here.
 *
 * Create/edit happens on dedicated pages (/paints/new, /paints/:id/edit) — the
 * library page only handles list/delete and routes to those pages.
 */
const PaintLibrary = () => {
    const navigate = useNavigate();
    const [paints, setPaints] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [paintToDelete, setPaintToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    // Load-more pagination, mirroring RoomDetails: `offset` is the NEXT offset
    // to request (envelope offset + limit), not the one already fetched.
    const [pagination, setPagination] = useState({ offset: 0, hasMore: false, total: 0 });
    const [loadingMore, setLoadingMore] = useState(false);
    // Request-generation guard: a delete-triggered refetch (replace) can race
    // an in-flight load-more (append). Without the guard, the stale append
    // response lands after the reset and splices old page data onto the fresh
    // list. Every replace-fetch bumps the generation; responses from an older
    // generation are dropped.
    const fetchGenerationRef = useRef(0);

    const fetchPaints = useCallback(async (offset = 0, append = false) => {
        if (!append) {
            fetchGenerationRef.current += 1;
            setLoading(true);
            setError(null);
        }
        const generation = fetchGenerationRef.current;
        try {
            const data = await PaintService.listPaints({ limit: PAGE_SIZE, offset });
            if (generation !== fetchGenerationRef.current) return; // stale response
            const list = data?.paints || [];
            setPaints(prev => (append ? [...prev, ...list] : list));
            if (data?.pagination) {
                setPagination({
                    offset: data.pagination.offset + data.pagination.limit,
                    hasMore: data.pagination.has_more,
                    total: data.pagination.total,
                });
            } else {
                // Defensive: a response without the envelope means we can't
                // page — treat what we got as the full set.
                setPagination({ offset: offset + list.length, hasMore: false, total: list.length });
            }
        } catch (err) {
            if (generation !== fetchGenerationRef.current) return; // stale failure
            console.error('Failed to load paints:', err);
            if (append) {
                toast.error(err.message || 'Failed to load more paints');
            } else {
                setError(err.message || 'Failed to load paints');
            }
        } finally {
            if (!append && generation === fetchGenerationRef.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPaints();
    }, [fetchPaints]);

    const loadMorePaints = async () => {
        setLoadingMore(true);
        await fetchPaints(pagination.offset, true);
        setLoadingMore(false);
    };

    const handleDelete = async () => {
        if (!paintToDelete) return;
        setDeleting(true);
        try {
            await PaintService.deletePaint(paintToDelete.id);
            toast.success(`Deleted ${paintToDelete.color_name || 'paint'}`);
            setPaintToDelete(null);
            fetchPaints();
        } catch (err) {
            toast.error(err.message || 'Failed to delete paint');
        } finally {
            setDeleting(false);
        }
    };

    // The library card shows ONLY the primary image (a single swatch), not the
    // full carousel — the grid stays scannable. The full gallery lives on the
    // edit page. Fall back to the first image if nothing is marked primary
    // (defensive — the server always promotes one, but be safe on race windows).
    const getPrimaryImageUrl = (paint) => {
        const primary = paint.images?.find(i => i.is_primary);
        return primary?.url || paint.images?.[0]?.url || null;
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-[var(--text-main)] flex items-center gap-2 theme-transition">
                        <Palette size={24} />
                        Paint Library
                    </h1>
                    <p className="text-sm text-[var(--text-secondary)] theme-transition">
                        Every paint you&apos;ve added. Use the &quot;Add Paint&quot; button on a room to attach a paint to a surface.
                    </p>
                </div>
                <button
                    onClick={() => navigate('/paints/new')}
                    className="flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg font-medium hover:opacity-90 transition"
                >
                    <Plus size={18} />
                    Add Paint
                </button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--primary)]"></div>
                </div>
            ) : error ? (
                <div className="text-center py-12 text-[var(--text-muted)]">{error}</div>
            ) : paints.length === 0 ? (
                <Card className="p-12 text-center">
                    <Palette size={48} className="mx-auto text-[var(--text-muted)] mb-3" />
                    <h3 className="text-lg font-medium text-[var(--text-main)] mb-1 theme-transition">
                        No paints yet
                    </h3>
                    <p className="text-sm text-[var(--text-secondary)] mb-4 theme-transition">
                        Scan a paint label or add one manually to start your library.
                    </p>
                    <button
                        onClick={() => navigate('/paints/new')}
                        className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg font-medium hover:opacity-90 transition"
                    >
                        Add your first paint
                    </button>
                </Card>
            ) : (
                // Portrait card grid — narrower than wider per the design.
                // 4 cards per row on desktop reads like a paint-chip wall.
                <>
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {paints.map(paint => {
                        const imageUrl = getPrimaryImageUrl(paint);
                        return (
                            <Card
                                key={paint.id}
                                data-testid={`paint-card-${paint.id}`}
                                className="overflow-hidden cursor-pointer hover:shadow-md transition"
                                onClick={() => navigate(`/paints/${paint.id}/edit`)}
                            >
                                {/* Portrait image area (aspect 4:5). Renders the
                                    primary swatch; falls back to a name-as-placeholder
                                    block when the paint has no images yet. */}
                                {imageUrl ? (
                                    <div className="aspect-[4/5] w-full bg-[var(--bg-hover)]">
                                        <img
                                            src={imageUrl}
                                            alt={`${paint.color_name || 'Paint'} label`}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                ) : (
                                    <div className="aspect-[4/5] w-full bg-[var(--bg-hover)] flex items-center justify-center theme-transition px-2">
                                        <span className="text-xs text-[var(--text-muted)] uppercase tracking-wide text-center">
                                            {paint.color_name || 'Unnamed'}
                                        </span>
                                    </div>
                                )}
                                <div className="p-3">
                                    <div className="flex justify-between items-start mb-1 gap-2">
                                        <div className="min-w-0">
                                            <h3 className="font-headline font-bold text-sm text-[var(--text-main)] truncate theme-transition">
                                                {paint.color_name || 'Unnamed paint'}
                                            </h3>
                                            {paint.manufacturer && (
                                                <p className="text-xs text-[var(--text-secondary)] truncate theme-transition">
                                                    {paint.manufacturer}
                                                    {paint.sheen && ` — ${paint.sheen}`}
                                                </p>
                                            )}
                                        </div>
                                        {/* Action buttons stop card-click propagation so the
                                            row click navigates to edit while these run their
                                            own handlers. */}
                                        <div className="flex gap-1 shrink-0">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/paints/${paint.id}/edit`);
                                                }}
                                                className="p-1.5 rounded-md hover:bg-[var(--bg-hover)] text-[var(--text-muted)] hover:text-[var(--primary)] transition theme-transition"
                                                title="Edit"
                                                aria-label={`Edit ${paint.color_name || 'paint'}`}
                                            >
                                                <Pencil size={14} />
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setPaintToDelete(paint);
                                                }}
                                                className="p-1.5 rounded-md hover:bg-red-50 text-[var(--text-muted)] hover:text-red-500 transition theme-transition"
                                                title="Delete from library"
                                                aria-label={`Delete ${paint.color_name || 'paint'}`}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </div>
                                    {paint.color_code && (
                                        <p className="text-xs font-mono text-[var(--text-muted)] mt-1 truncate">
                                            {paint.color_code}
                                        </p>
                                    )}
                                </div>
                            </Card>
                        );
                    })}
                </div>
                {pagination.hasMore && (
                    <div className="text-center space-y-2">
                        <p className="text-xs text-[var(--text-muted)] theme-transition">
                            Showing {paints.length} of {pagination.total}
                        </p>
                        <button
                            onClick={loadMorePaints}
                            disabled={loadingMore}
                            className="px-6 py-2.5 bg-[var(--bg-hover)] text-[var(--text-main)] rounded-xl hover:bg-[var(--bg-elevated)] transition font-medium text-sm disabled:opacity-50"
                        >
                            {loadingMore ? 'Loading...' : 'Load more'}
                        </button>
                    </div>
                )}
                </>
            )}

            <ConfirmationModal
                isOpen={paintToDelete !== null}
                onClose={() => setPaintToDelete(null)}
                onConfirm={handleDelete}
                isLoading={deleting}
                title="Delete this paint?"
                message={
                    <>
                        This removes <strong>{paintToDelete?.color_name || 'this paint'}</strong> from your library
                        and detaches it from every room that uses it. All photos are also deleted from storage.
                        This can&apos;t be undone.
                    </>
                }
                confirmText={deleting ? 'Deleting...' : 'Delete'}
                isDanger={true}
                icon={Trash2}
            />
        </div>
    );
};

export default PaintLibrary;
